import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FaArrowLeft, FaCalendar, FaUser, FaTag, FaClock, FaTerminal } from 'react-icons/fa';

const API_URL = 'https://arap-backend.onrender.com/api/blog';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await axios.get(`${API_URL}/${slug}`);
        setPost(res.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError(err.response?.status === 404 ? 'Post not found.' : 'Failed to load post.');
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const readTime = (content) => {
    const words = content.split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <FaTerminal className="text-4xl text-cyber-green mx-auto mb-4 animate-pulse" />
          <p className="text-cyber-green font-mono">{'>'} Decrypting dispatch...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="terminal-window max-w-md">
          <div className="terminal-header">
            <div className="terminal-dot bg-cyber-red"></div>
            <div className="terminal-dot bg-cyber-amber"></div>
            <div className="terminal-dot bg-cyber-green"></div>
            <span className="ml-3 text-xs text-cyber-muted">error</span>
          </div>
          <div className="terminal-body text-center py-8">
            <p className="text-cyber-red mb-4">[ERROR] {error}</p>
            <Link to="/blog" className="text-cyber-cyan hover:text-cyber-green transition-colors">
              {'<'}- Return to dispatches
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative pt-20 pb-20 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-cyber-muted hover:text-cyber-green transition-colors font-mono"
          >
            <FaArrowLeft /> cd ../blog
          </Link>
        </motion.div>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Cover Image */}
          {post.coverImage && (
            <div className="rounded-lg overflow-hidden mb-8 border border-cyber-green/20">
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-64 md:h-96 object-cover"
              />
            </div>
          )}

          {/* Title & Meta */}
          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-cyber-muted mb-4">
              <span className="flex items-center gap-1">
                <FaUser className="text-cyber-green" />
                {post.author}
              </span>
              <span className="flex items-center gap-1">
                <FaCalendar className="text-cyber-green" />
                {formatDate(post.createdAt)}
              </span>
              <span className="flex items-center gap-1">
                <FaClock className="text-cyber-green" />
                {readTime(post.content)} min read
              </span>
            </div>

            {post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2 py-1 rounded bg-cyber-green/10 text-cyber-green border border-cyber-green/20 flex items-center gap-1"
                  >
                    <FaTag className="text-[10px]" /> {tag}
                  </span>
                ))}
              </div>
            )}
          </header>

          <div className="h-px bg-gradient-to-r from-cyber-green/50 via-cyber-cyan/30 to-transparent mb-8"></div>

          {/* Content */}
          <div className="blog-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content}
            </ReactMarkdown>
          </div>

          <div className="h-px bg-gradient-to-r from-cyber-green/50 via-cyber-cyan/30 to-transparent mt-12 mb-8"></div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <Link
              to="/blog"
              className="text-cyber-cyan hover:text-cyber-green transition-colors font-mono text-sm"
            >
              {'<'}- Back to all dispatches
            </Link>
            {post.updatedAt && post.updatedAt !== post.createdAt && (
              <span className="text-xs text-cyber-muted">
                Last updated: {formatDate(post.updatedAt)}
              </span>
            )}
          </div>
        </motion.article>
      </div>
    </div>
  );
};

export default BlogPost;
